/**
 * OnboardingChecklist.js — онбординг-чеклист на Дашборде. Один компонент на все
 * четыре роли.
 *
 * Раньше их было два — админский и WorkerChecklist — с расходящейся логикой
 * пропуска и скрытия. Состав шагов теперь приходит с сервера, и держать две
 * реализации одного ответа значит гарантированно их развести.
 *
 * Данные: GET /api/onboarding/checklist → { role, steps[], dismissed, tutorial_* }.
 * Список уже отфильтрован по роли, стране орги и тумблерам модулей — фронт
 * правил не знает (см. backend/config/onboardingSteps.js).
 *
 * Шаг закрывается либо реальным действием (`done` с сервера), либо кнопкой
 * «Уже сделал» (`skipped`). У обязательного шага (`required`) кнопки нет —
 * и на сервере POST /onboarding/skip такой шаг отклоняет: спрятанная кнопка не
 * защита. Сам блок свернуть и скрыть можно всегда, обязательность на это не
 * влияет — давить блоком на экране мы не хотим.
 *
 * PostHog:
 *   onboarding_checklist_step_clicked / _step_skipped / _dismissed
 *   onboarding_checklist_collapsed / _all_done
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { track, Analytics } from '../analytics';
import { api } from '../api/client';
import { BRAND_NAME } from '../branding';
import { personalDocConfig } from '../config/personalDoc';
import { onboardingStepAction, stepLabelKey } from '../config/onboardingActions';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import AddToHomeScreenModal from './AddToHomeScreenModal';

// Роли, у которых нет прав на org_settings/team — вкладка Настройки → Организация
// (SettingsPage.js: isAdmin = canAny('org_settings') || canAny('team')) им не
// рендерится вовсе. Для них шаг home_screen ставит PWA прямо здесь, не уводя
// на страницу, которую они не увидят.
const HOME_SCREEN_INLINE_ROLES = ['foreman', 'worker'];

const COLLAPSE_KEY = 'pm_checklist_collapsed';

// До конструктора прогресс жил в localStorage. Переносим его в БД при первом
// заходе — иначе живой тенант увидит закрытые полгода назад шаги заново, а тот,
// кто чеклист скрыл, получит его обратно на весь экран.
function readLegacy(orgId) {
  const skipKeys    = [`pm_checklist_skipped_${orgId}`, 'pm_worker_checklist_skipped'];
  const dismissKeys = [`pm_checklist_dismissed_${orgId}`, 'pm_worker_checklist_dismissed'];

  const steps = [];
  for (const k of skipKeys) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) steps.push(...arr.filter((x) => typeof x === 'string'));
    } catch { /* битый JSON — метки не критичны, просто игнорируем */ }
  }

  const dismissed = dismissKeys.some((k) => localStorage.getItem(k) === '1');

  return {
    keys: [...skipKeys, ...dismissKeys, `pm_checklist_dismissed_${orgId}_done`],
    steps: [...new Set(steps)],
    dismissed,
    found: steps.length > 0 || dismissed,
  };
}

export default function OnboardingChecklist({
  onClientAdd, onPropertyAdd, onWorkerInvite, onTaskCreate, onLogoUpload,
  onAllDone, onDismissed,
}) {
  const { t }    = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const doc      = personalDocConfig(user?.org_country);

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const { canInstall, isIOS, promptInstall } = useInstallPrompt();

  // Ref, чтобы эффект «всё готово» не тянул колбэк в зависимости и не срабатывал
  // повторно на каждый ререндер родителя.
  const onAllDoneRef = useRef(onAllDone);
  useEffect(() => { onAllDoneRef.current = onAllDone; });
  const onDismissedRef = useRef(onDismissed);
  useEffect(() => { onDismissedRef.current = onDismissed; });
  const calledAllDoneRef = useRef(false);
  const migratedRef = useRef(false);

  // Пользователь уже нажал «Закрыть» в этой сессии. Ответ `/checklist`, ушедший
  // в сеть ДО записи метки, вернёт dismissed:false и, придя позже, воскресит
  // блок на экране. Локальное решение человека сильнее любого ответа сервера —
  // сервер тут только источник первоначального состояния.
  const dismissedLocallyRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/onboarding/checklist');
      setData(res && dismissedLocallyRef.current ? { ...res, dismissed: true } : res);

      // ⚠️ «Всё готово» считаем ЗДЕСЬ, а не только в эффекте ниже, и обязательно
      // ДО onDismissed.
      //
      // Родитель снимает компонент с экрана, как только услышит onDismissed, —
      // и эффект `[allDone]` у скрытого блока уже не выполнится. Из-за этого
      // родитель навсегда оставался в состоянии «скрыт, но не завершён» и на
      // каждом заходе рисовал плашку «Начните работу → Начало работы».
      // Человек закрыл полностью пройденный онбординг, разлогинился, вернулся —
      // и снова видит приглашение начать работу.
      //
      // Оба колбэка зовём подряд, синхронно: React сложит их в один рендер, и
      // плашка не успеет мигнуть.
      const list = res?.steps || [];
      if (list.length && list.every((s) => s.done || s.skipped) && !calledAllDoneRef.current) {
        calledAllDoneRef.current = true;
        track('onboarding_checklist_all_done', { role: res?.role });
        onAllDoneRef.current?.();
      }

      // Блок скрыт с прошлого захода (метка на сервере) — сообщаем Дашборду,
      // чтобы он решил, показывать ли ссылку возврата. Сам компонент отрисует null.
      if (res?.dismissed) onDismissedRef.current?.();
    } catch {
      // Тихо — чеклист не должен мешать работе Дашборда.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      // Разовый перенос localStorage → БД. Ключи чистим только после успешных
      // ответов: иначе оборванный запрос молча стирает прогресс насовсем.
      if (!migratedRef.current && user.organization_id) {
        migratedRef.current = true;
        const legacy = readLegacy(user.organization_id);
        if (legacy.found) {
          try {
            if (legacy.steps.length) await api.post('/onboarding/skip', { step_keys: legacy.steps });
            if (legacy.dismissed)    await api.post('/onboarding/dismiss', {});
            legacy.keys.forEach((k) => localStorage.removeItem(k));
          } catch { /* попробуем при следующем заходе */ }
        } else {
          legacy.keys.forEach((k) => localStorage.removeItem(k));
        }
      }
      if (!cancelled) await load();
    })();

    return () => { cancelled = true; };
  }, [user, load]);

  // Прогресс мог измениться на другой вкладке — перечитываем по возврату.
  useEffect(() => {
    const handler = () => { if (user) load(); };
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, [user, load]);

  const steps = data?.steps || [];
  const isDone    = (s) => s.done || s.skipped;
  const doneCount = steps.filter(isDone).length;
  const allDone   = steps.length > 0 && doneCount === steps.length;

  // Шаги могли закрыться уже по ходу сессии (нажал «Уже сделал» или сходил и
  // сделал). Первичный случай «всё было готово ещё до загрузки» ловится в load()
  // — там это надо успеть до onDismissed. `calledAllDoneRef` не даёт задвоить.
  useEffect(() => {
    if (allDone && !calledAllDoneRef.current) {
      calledAllDoneRef.current = true;
      track('onboarding_checklist_all_done', { role: data?.role });
      onAllDoneRef.current?.();
    }
  }, [allDone, data]);

  const handleSkip = async (key) => {
    track('onboarding_checklist_step_skipped', { step: key, role: data?.role });
    // Оптимистично — шаг закрывается мгновенно, сервер лишь подтверждает.
    setData((d) => (d ? { ...d, steps: d.steps.map((s) => (s.key === key ? { ...s, skipped: true } : s)) } : d));
    try {
      await api.post('/onboarding/skip', { step_key: key });
    } catch {
      setData((d) => (d ? { ...d, steps: d.steps.map((s) => (s.key === key ? { ...s, skipped: false } : s)) } : d));
    }
  };

  // ⚠️ Прячем блок ТОЛЬКО после подтверждения сервера.
  //
  // Раньше здесь стоял оптимистичный порядок: сначала спрятать, потом записать.
  // При неудачном запросе откатывать было уже нечего — родитель успевал снять
  // компонент с экрана, — и человек получал молчаливую ложь: блок исчез сейчас,
  // а на следующем заходе вернулся. Ошибку записи надо показывать, а не прятать.
  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    track('onboarding_checklist_dismissed', { role: data?.role, done: doneCount, total: steps.length });
    try {
      await api.post('/onboarding/dismiss', {});
      dismissedLocallyRef.current = true;
      setData((d) => (d ? { ...d, dismissed: true } : d));
      onDismissed?.();
    } catch (e) {
      toast.error(e?.message || t('onboarding.checklist.dismissFailed'));
    } finally {
      setDismissing(false);
    }
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    track('onboarding_checklist_collapsed', { collapsed: next, role: data?.role });
  };

  // home_screen у Бригадира/Работника: та же логика, что в SettingsPage →
  // OrgTab.handleInstallClick, но без перехода в Настройки (см. константу выше).
  const handleHomeScreenInstall = async () => {
    const platform = canInstall ? 'android' : isIOS ? 'ios' : 'unsupported';
    Analytics.homeScreenInstallClicked({ platform, source: 'checklist' });
    if (canInstall) {
      const res = await promptInstall();
      Analytics.homeScreenInstallResult({ outcome: res.outcome || (res.ok ? 'accepted' : 'dismissed') });
      return;
    }
    if (isIOS) { setIosModalOpen(true); return; }
    toast.error(t('settings.homeScreen.unsupported'));
  };

  const handleAction = (key) => {
    track('onboarding_checklist_step_clicked', { step: key, role: data?.role });
    if (key === 'home_screen' && HOME_SCREEN_INLINE_ROLES.includes(data?.role)) {
      return handleHomeScreenInstall();
    }
    onboardingStepAction(key, {
      navigate,
      handlers: { onClientAdd, onPropertyAdd, onWorkerInvite, onTaskCreate, onLogoUpload },
      tutorial: { taskId: data?.tutorial_task_id, propertyId: data?.tutorial_property_id },
    });
  };

  // ── Guard: не показываем ────────────────────────────────────────────────────
  if (!user || user.is_demo || !user.setup_completed) return null;
  if (loading || !data || data.dismissed || !steps.length) return null;

  const progressPct = Math.round((doneCount / steps.length) * 100);

  return (
    <>
    {iosModalOpen && <AddToHomeScreenModal onClose={() => setIosModalOpen(false)} />}
    <div
      style={{
        background: 'var(--bg2)',
        border: '1px solid var(--border, #e0e0e8)',
        borderRadius: 14,
        padding: '18px 20px',
        marginBottom: 20,
      }}
      data-ph-capture="onboarding_checklist_viewed"
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: collapsed ? 0 : 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
            {allDone ? `🎉 ${t('onboarding.checklist.allDone')}` : t('onboarding.checklist.title')}
          </div>
          {!allDone && (
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>
              {t('onboarding.checklist.subtitle')} · {doneCount}/{steps.length}
            </div>
          )}
        </div>
        <button
          onClick={allDone ? handleDismiss : toggleCollapse}
          disabled={dismissing}
          data-ph-id="onboarding-checklist-header-action"
          style={{
            background: 'none', border: 'none',
            color: 'var(--text3)', cursor: 'pointer',
            fontSize: 12, padding: '2px 6px', flexShrink: 0,
          }}
        >
          {allDone
            ? t('onboarding.checklist.close')
            : (collapsed ? t('onboarding.checklist.expand') : t('onboarding.checklist.collapse'))}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Progress bar */}
          <div style={{
            height: 4, borderRadius: 2,
            background: 'var(--border, #e0e0e8)',
            marginBottom: 16, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${progressPct}%`,
              background: allDone ? 'var(--green, #22c55e)' : 'var(--accent)',
              borderRadius: 2,
              transition: 'width 0.4s ease',
            }} />
          </div>

          {/* Steps */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {steps.map((step) => {
              const effectDone = isDone(step);
              const labelKey   = stepLabelKey(step, doc.type);

              return (
                <div
                  key={step.key}
                  className="onb-step"
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: effectDone ? 'var(--bg2, #f5f5f7)' : 'transparent',
                    border: effectDone ? 'none' : '1px solid var(--border, #e0e0e8)',
                    opacity: effectDone ? 0.65 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {/* Зелёная галка — подтверждено данными, серая — «Уже сделал» */}
                  <div className="onb-step__check" style={{
                    background: step.done
                      ? 'var(--green, #22c55e)'
                      : step.skipped
                        ? 'var(--text3, #aaa)'
                        : 'var(--bg2, #f5f5f7)',
                    border: effectDone ? 'none' : '2px solid var(--border, #d0d0d8)',
                  }}>
                    {effectDone ? '✓' : ''}
                  </div>

                  <div className="onb-step__body">
                    <div className="onb-step__text">
                      <div style={{
                        fontSize: 13, fontWeight: 600,
                        color: 'var(--text)',
                        textDecoration: effectDone ? 'line-through' : 'none',
                      }}>
                        {t(labelKey)}
                        {step.required && !effectDone && (
                          <span style={{
                            marginLeft: 6, fontSize: 10, fontWeight: 700,
                            color: 'var(--accent)', textTransform: 'uppercase',
                            letterSpacing: 0.3,
                          }}>
                            {t('onboarding.checklist.requiredBadge')}
                          </span>
                        )}
                      </div>
                      {!effectDone && (
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 1 }}>
                          {t(`onboarding.steps.${step.key}.hint`, { brand: BRAND_NAME })}
                        </div>
                      )}
                    </div>

                    {!effectDone && (
                      <div className="onb-step__actions">
                        <button
                          onClick={() => handleAction(step.key)}
                          className="onb-step__cta"
                          data-ph-id="onboarding-checklist-cta"
                          data-ph-capture-attribute-checklist-step={step.key}
                        >
                          {t(`onboarding.steps.${step.key}.cta`)}
                        </button>
                        {/* Обязательный шаг закрывается только делом */}
                        {!step.required && (
                          <button
                            onClick={() => handleSkip(step.key)}
                            className="onb-step__skip"
                            data-ph-id="onboarding-checklist-skip"
                            data-ph-capture-attribute-checklist-step={step.key}
                          >
                            {t('onboarding.checklist.alreadyDone')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
    </>
  );
}
